"use client";

import {
  Box,
  Container,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
// import { MediaPlayer, MediaProvider, Poster } from "@vidstack/react";
const VISUALIZE__URL = import.meta.env.VITE_VISUALIZE__URL;
const VITE_HOST_NAME = import.meta.env.VITE_HOST_NAME;

// Generic domain videos
const landingVideo1 = "generic/landing_page_generic_1.mp4";
const landingVideo2 = "generic/landing_page_generic_2.mp4";
const landingvideo1_mbl = "generic/landing_page_generic_mobile1.mp4";
const landingvideo2_mbl = "generic/landing_page_generic_mobile2.mp4";
// ENT domain videos
const entLandingVideo1 = "ENT/landing_page_ent_1.mp4";
const entLandingVideo2 = "ENT/landing_page_ent_2.mp4";
const entlandingvideo1_mbl = "ENT/landing_page_ent_mobile1.mp4";
const entlandingvideo2_mbl = "ENT/landing_page_ent_mobile2.mp4";

const BannerVideoSlider = () => {
  console.log("process.env.NODE_ENV", process.env.NODE_ENV);

  const { t } = useTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoList, setVideoList] = useState<string[]>([]);
  const [activePlayer, setActivePlayer] = useState<number>(0);
  const [playerSources, setPlayerSources] = useState<[string, string]>([
    "",
    "",
  ]);
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([null, null]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const host = window.location.host;
  const isEntDomain = host.includes(VITE_HOST_NAME);

  // Get available videos based on device and domain
  const getVideoList = (): string[] => {
    const desktopVideos = isEntDomain
      ? [entLandingVideo1, entLandingVideo2]
      : [landingVideo1, landingVideo2];

    const mobileVideos = isEntDomain
      ? [entlandingvideo1_mbl, entlandingvideo2_mbl]
      : [landingvideo1_mbl, landingvideo2_mbl];

    return isMobile ? mobileVideos : desktopVideos;
  };

  // Get a random video that's different from the current one
  const getRandomDifferentVideo = (currentVideo: string): string => {
    const videos = videoList;
    if (videos.length <= 1) return videos[0] || "";

    const filteredVideos = videos.filter((video) => video !== currentVideo);
    return filteredVideos[Math.floor(Math.random() * filteredVideos.length)];
  };

  // Initialize video list and first video
  useEffect(() => {
    const videos = getVideoList();
    setVideoList(videos);

    if (videos.length > 0) {
      const initialVideo = videos[Math.floor(Math.random() * videos.length)];
      const nextVideo = getRandomDifferentVideo(initialVideo);

      setPlayerSources([initialVideo, nextVideo]);
    }
  }, [isMobile, isEntDomain]);

  // Handle video end event
  const handleVideoEnd = () => {
    console.log("Video ended, switching to next video");

    // Get the current active source
    const currentSource = playerSources[activePlayer];

    // Get a random different video for the next player
    const nextVideo = getRandomDifferentVideo(currentSource);

    // Update the sources - keep the current player's source, update the next player's source
    const newSources = [...playerSources] as [string, string];
    const nextPlayerIndex = activePlayer === 0 ? 1 : 0;
    newSources[nextPlayerIndex] = nextVideo;

    setPlayerSources(newSources);

    // Toggle the active player
    setActivePlayer(nextPlayerIndex);

    // Force the new active player to play
    setTimeout(() => {
      if (videoRefs.current[nextPlayerIndex]) {
        videoRefs.current[nextPlayerIndex]?.play();
      }
    }, 0);
  };

  return (
    <>
      <Box
        sx={{
          position: "relative",
          height: { xs: "100vh", lg: "100vh" },
          overflow: "hidden",
        }}
      >
        {videoList.length > 0 && (
          <>
            {/* Video element as the first layer */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                zIndex: 0,
              }}
            >
              {[0, 1].map((playerIndex) => {
                const isActive = playerIndex === activePlayer;
                const videoSrc = playerSources[playerIndex];

                return (
                  <video
                    key={`video-${playerIndex}`}
                    ref={(el) => {
                      videoRefs.current[playerIndex] = el;
                    }}
                    src={videoSrc ? `${VISUALIZE__URL}${videoSrc}` : undefined}
                    autoPlay={isActive}
                    playsInline
                    muted
                    preload="auto"
                    onPlay={() => isActive && setIsPlaying(true)}
                    onEnded={isActive ? handleVideoEnd : undefined}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      position: "absolute",
                      top: 0,
                      left: 0,
                      opacity: isActive ? 1 : 0,
                      transition: "opacity 0.6s ease-in-out",
                      pointerEvents: isActive ? "auto" : "none",
                    }}
                  />
                );
              })}
            </Box>
            {/* Overlay for dull effect */}
            {isPlaying && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: { xs: "100vh", lg: "100vh" },
                  background:
                    "linear-gradient(to bottom, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4))",
                  zIndex: 1,
                }}
              />
            )}
          </>
        )}
        {isPlaying && (
          <Container
            sx={{
              position: "relative",
              zIndex: 2,
              display: { xs: "flex", lg: "flex" },
              flexDirection: "column",
              justifyContent: "center",
              alignItems: { xs: "center", sm: "center", lg: "flex-start" },
              height: { xs: "100vh", lg: "100vh" },
              width: "100%",
              maxWidth: "700px",
              ml: { xs: 0, sm: 5, md: 9 },
              mr: { xs: 0, sm: 5, md: 9 },
              mt: { xs: 27, sm: 35, md: 0 },
              opacity: 1,
            }}
          >
            <Box>
              <Typography
                color="white"
                sx={{
                  mb: 3,
                  fontSize: {
                    xs: "25px",
                    sm: "25px",
                    md: "30px",
                    lg: "40px",
                  },
                  textAlign: {
                    xs: "left",
                    sm: "left",
                    md: "center",
                    lg: "left",
                  },
                  fontWeight: "900",
                  lineHeight: { xs: 1, sm: 1, md: 1 },
                  opacity:0,
                  animation: "fadeIn 1s ease-in-out 0.8s forwards",
                  "@keyframes fadeIn": {
                    from: { opacity: 0, transform: "translateY(20px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                  },
                }}
              >
                {t("talk_less_show_more")}
              </Typography>
              <Typography
                color="white"
                align="left"
                fontWeight="400"
                sx={{
                  mb: 3,
                  pl: 0,
                  fontSize: {
                    xs: "17px",
                    sm: "18px",
                    md: "18px",
                    lg: "22px",
                  },
                  textAlign: {
                    xs: "left",
                    sm: "left",
                    md: "center",
                    lg: "left",
                  },
                  lineHeight: { xs: 1.4, sm: 1.5, md: 1.5 },
                  maxWidth: { xs: "100%", sm: "100%", md: "100%", lg: "60%" },
                  width: "100%",
                  opacity: 0,
                  animation: "fadeIn 1s ease-in-out 0.8s forwards",
                  "@keyframes fadeIn": {
                    from: { opacity: 0, transform: "translateY(20px)" },
                    to: { opacity: 1, transform: "translateY(0)" },
                  },
                }}
              >
                {t("transforming_communication_experience ")}
              </Typography>
            </Box>
          </Container>
        )}
        {/* Content container - moved to left side */}
      </Box>
    </>
  );
};

export default BannerVideoSlider;

// "use client";

// import {
//   Box,
//   Container,
//   Typography,
//   useMediaQuery,
//   useTheme,
// } from "@mui/material";
// import { useTranslation } from "react-i18next";
// import { useEffect, useRef, useState } from "react";
// // import { MediaPlayer, MediaProvider, Poster } from "@vidstack/react";
// const VISUALIZE__URL = import.meta.env.VITE_VISUALIZE__URL;
// const VITE_HOST_NAME = import.meta.env.VITE_HOST_NAME;

// // Generic domain videos
// const landingVideo1 = "generic/landing_page_generic_1.mp4";
// const landingVideo2 = "generic/landing_page_generic_2.mp4";
// const landingvideo1_mbl = "generic/landing_page_generic_mobile1.mp4";
// const landingvideo2_mbl = "generic/landing_page_generic_mobile2.mp4";
// // ENT domain videos
// const entLandingVideo1 = "ENT/landing_page_ent_1.mp4";
// const entLandingVideo2 = "ENT/landing_page_ent_2.mp4";
// const entlandingvideo1_mbl = "ENT/landing_page_ent_mobile1.mp4";
// const entlandingvideo2_mbl = "ENT/landing_page_ent_mobile2.mp4";

// const BannerVideoSlider = () => {
//   console.log("process.env.NODE_ENV", process.env.NODE_ENV);

//   const { t } = useTranslation();
//   const [isPlaying, setIsPlaying] = useState(false);
//   const [videoList, setVideoList] = useState<string[]>([]);
//   const [activePlayer, setActivePlayer] = useState<number>(0);
//   const [playerSources, setPlayerSources] = useState<[string, string]>([
//     "",
//     "",
//   ]);
//   const videoRefs = useRef<(HTMLVideoElement | null)[]>([null, null]);

//   const theme = useTheme();
//   const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

//   const host = window.location.host;
//   const isEntDomain = host.includes(VITE_HOST_NAME);

//   // Get available videos based on device and domain
//   const getVideoList = (): string[] => {
//     const desktopVideos = isEntDomain
//       ? [entLandingVideo1, entLandingVideo2]
//       : [landingVideo1, landingVideo2];

//     const mobileVideos = isEntDomain
//       ? [entlandingvideo1_mbl, entlandingvideo2_mbl]
//       : [landingvideo1_mbl, landingvideo2_mbl];

//     return isMobile ? mobileVideos : desktopVideos;
//   };

//   // Get a random video that's different from the current one
//   const getRandomDifferentVideo = (currentVideo: string): string => {
//     const videos = videoList;
//     if (videos.length <= 1) return videos[0] || "";

//     const filteredVideos = videos.filter((video) => video !== currentVideo);
//     return filteredVideos[Math.floor(Math.random() * filteredVideos.length)];
//   };

//   // Initialize video list and first video
//   useEffect(() => {
//     const videos = getVideoList();
//     setVideoList(videos);

//     if (videos.length > 0) {
//       const initialVideo = videos[Math.floor(Math.random() * videos.length)];
//       const nextVideo = getRandomDifferentVideo(initialVideo);

//       setPlayerSources([initialVideo, nextVideo]);
//     }
//   }, [isMobile, isEntDomain]);

//   // Handle video end event
//   const handleVideoEnd = () => {
//     console.log("Video ended, switching to next video");

//     // Get the current active source
//     const currentSource = playerSources[activePlayer];

//     // Get a random different video for the next player
//     const nextVideo = getRandomDifferentVideo(currentSource);

//     // Update the sources - keep the current player's source, update the next player's source
//     const newSources = [...playerSources] as [string, string];
//     const nextPlayerIndex = activePlayer === 0 ? 1 : 0;
//     newSources[nextPlayerIndex] = nextVideo;

//     setPlayerSources(newSources);

//     // Toggle the active player
//     setActivePlayer(nextPlayerIndex);

//     // Force the new active player to play
//     setTimeout(() => {
//       if (videoRefs.current[nextPlayerIndex]) {
//         videoRefs.current[nextPlayerIndex]?.play();
//       }
//     }, 0);
//   };

//   return (
//     <>
//       <Box
//         sx={{
//           position: "relative",
//           height: { xs: "100vh", lg: "100vh" },
//           overflow: "hidden",
//         }}
//       >
//         {videoList.length > 0 && (
//           <>
//             {/* Video element as the first layer */}
//             <Box
//               sx={{
//                 position: "absolute",
//                 top: 0,
//                 left: 0,
//                 width: "100vw",
//                 height: "100vh",
//                 zIndex: 0,
//               }}
//             >
//               {[0, 1].map((playerIndex) => {
//                 const isActive = playerIndex === activePlayer;
//                 const videoSrc = playerSources[playerIndex];

//                 return (
//                   <video
//                     key={`video-${playerIndex}`}
//                     ref={(el) => {
//                       videoRefs.current[playerIndex] = el;
//                     }}
//                     src={videoSrc ? `${VISUALIZE__URL}${videoSrc}` : undefined}
//                     autoPlay={isActive}
//                     playsInline
//                     muted
//                     preload="auto"
//                     onPlay={() => isActive && setIsPlaying(true)}
//                     onEnded={isActive ? handleVideoEnd : undefined}
//                     style={{
//                       width: "100%",
//                       height: "100%",
//                       objectFit: "cover",
//                       position: "absolute",
//                       top: 0,
//                       left: 0,
//                       opacity: isActive ? 1 : 0,
//                       transition: "opacity 0.6s ease-in-out",
//                       pointerEvents: isActive ? "auto" : "none",
//                     }}
//                   />
//                 );
//               })}
//             </Box>
//             {/* Overlay for dull effect */}
//             {isPlaying && (
//               <Box
//                 sx={{
//                   position: "absolute",
//                   top: 0,
//                   left: 0,
//                   width: "100vw",
//                   height: { xs: "100vh", lg: "100vh" },
//                   background:
//                     "linear-gradient(to bottom, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4))",
//                   zIndex: 1,
//                 }}
//               />
//             )}
//           </>
//         )}
//         {isPlaying && (
//           <Container
//             sx={{
//               position: "relative",
//               zIndex: 2,
//               display: { xs: "flex", lg: "flex" },
//               flexDirection: "column",
//               justifyContent: "center",
//               alignItems: { xs: "center", sm: "center", lg: "flex-start" },
//               height: { xs: "100vh", lg: "100vh" },
//               width: "100%",
//               maxWidth: "700px",
//               ml: { xs: 0, sm: 5, md: 9 },
//               mr: { xs: 0, sm: 5, md: 9 },
//               mt: { xs: 27, sm: 35, md: 0 },
//               opacity: 0,
//               animation: "fadeIn 1s ease-in-out 0.8s forwards",
//               "@keyframes fadeIn": {
//                 from: { opacity: 0, transform: "translateY(20px)" },
//                 to: { opacity: 1, transform: "translateY(0)" },
//               },
//             }}
//           >
//             <Box>
//               <Typography
//                 color="white"
//                 sx={{
//                   mb: 3,
//                   fontSize: {
//                     xs: "25px",
//                     sm: "25px",
//                     md: "30px",
//                     lg: "40px",
//                   },
//                   textAlign: {
//                     xs: "left",
//                     sm: "left",
//                     md: "center",
//                     lg: "left",
//                   },
//                   fontWeight: "900",
//                   lineHeight: { xs: 1, sm: 1, md: 1 },
//                 }}
//               >
//                 {t("talk_less_show_more")}
//               </Typography>
//               <Typography
//                 color="white"
//                 align="left"
//                 fontWeight="400"
//                 sx={{
//                   mb: 3,
//                   pl: 0,
//                   fontSize: {
//                     xs: "17px",
//                     sm: "18px",
//                     md: "18px",
//                     lg: "22px",
//                   },
//                   textAlign: {
//                     xs: "left",
//                     sm: "left",
//                     md: "center",
//                     lg: "left",
//                   },
//                   lineHeight: { xs: 1.4, sm: 1.5, md: 1.5 },
//                   maxWidth: { xs: "100%", sm: "100%", md: "100%", lg: "60%" },
//                   width: "100%",
//                 }}
//               >
//                 {t("transforming_communication_experience ")}
//               </Typography>
//             </Box>
//           </Container>
//         )}
//         {/* Content container - moved to left side */}
//       </Box>
//     </>
//   );
// };

// export default BannerVideoSlider;
